<template>
  <div>
    <!-- 状态/提醒类弹框：禁止点遮罩关闭，须点 OK / Later / View Detail / Submit -->
    <BrStatusModal
      :visible="showReminder"
      variant="reminder"
      scrim-action="none"
      :title="$t('lbl_EMPBE_BR_Reminder_Title')"
      :body="reminderModalBody"
      :secondary-label="$t('lbl_EMPBE_BR_Reminder_Later')"
      :primary-label="$t('lbl_EMPBE_BR_Reminder_SubmitNow')"
      @secondary="handleLater"
      @primary="handleSubmitNow"
    />
    <BrStatusModal
      :visible="showApproved"
      variant="approved"
      scrim-action="none"
      :title="$t('lbl_EMPBE_BR_ApprovedModal_Title')"
      :body="approvedModalBody"
      :secondary-label="$t('lbl_EMPBE_BR_ApprovedModal_Review')"
      :primary-label="$t('lbl_EMPBE_BR_ApprovedModal_Ok')"
      @secondary="() => closeStatus('approved', '/businessDocuments/default')"
      @primary="() => closeStatus('approved')"
    />
    <BrStatusModal
      :visible="showRejected"
      variant="rejected"
      scrim-action="none"
      :title="$t('lbl_EMPBE_BR_RejectedModal_Title')"
      :body="rejectedModalBody"
      :secondary-label="$t('lbl_EMPBE_BR_Reminder_Later')"
      :primary-label="$t('lbl_EMPBE_BR_Reminder_SubmitNow')"
      @secondary="() => closeStatus('rejected')"
      @primary="() => closeStatus('rejected', '/businessDocuments/addDocument')"
    />
    <BrStatusModal
      :visible="showExpiringSoon"
      variant="expiring"
      scrim-action="none"
      :title="$t('lbl_EMPBE_BR_ExpirySoonModal_Title')"
      :body="expiringSoonModalBody"
      :secondary-label="$t('lbl_EMPBE_BR_Reminder_Later')"
      :primary-label="$t('lbl_EMPBE_BR_Reminder_SubmitNow')"
      @secondary="() => closeExpiry()"
      @primary="() => closeExpiry('/businessDocuments/addDocument')"
    />
  </div>
</template>

<script>
import { mapState } from 'vuex';
import BrStatusModal from './BrStatusModal.vue';
import {
  acknowledgeBrExpirySoonNotification,
  acknowledgeBrStatusNotification,
  brRuntimeState,
  consumeBrPostLoginPromptPending,
  detectBrExpirySoonNotification,
  detectBrStatusNotification,
  dismissBrReminderForSession,
  dismissBrExpirySoonForToday,
  getEffectiveKybLockStartDate,
  hasBrPromptShownThisLogin,
  isKybLockPeriodActiveByCache,
  invalidateBrRuntimeCache,
  isBrHomeRoute,
  isBrReminderEnabled,
  loadBrSubmissionStatus,
  markBrPostLoginPromptPending,
  markBrPromptShownThisLogin,
  shouldRedirectLockedBrRoute,
  shouldShowBrReminder,
} from '../../../constants/businessRegistration';
import { formatBrLocaleLongDate, parseBrLocalDate } from '../utils/brDocument';

export default {
  name: 'BrPromptHost',
  components: { BrStatusModal },
  data() {
    return {
      showReminder: false,
      showApproved: false,
      showRejected: false,
      showExpiringSoon: false,
      rejectedModalBody: '',
      expiringSoonModalBody: '',
      bootstrapToken: 0,
      deferExpiryModalThisSession: false,
      shownResultApprovalId: '',
    };
  },
  computed: {
    ...mapState('authentication', ['user']),
    brUserKey() {
      const id = this.user?.empCompanyId ?? this.user?.EmpCompanyId;
      return id != null ? String(id) : '';
    },
    reminderModalBody() {
      // 截止日期 = 后台 Kyb.LockStartDate（经 AccessGate.lockStartDate）
      const raw = getEffectiveKybLockStartDate();
      const parsed = parseBrLocalDate(raw);
      const dateText = parsed
        ? formatBrLocaleLongDate(parsed, this.$i18n?.locale)
        : (raw || '');
      return this.$t('lbl_EMPBE_BR_Reminder_Body', { date: dateText });
    },
    /** 過渡期後（鎖定期已生效）用完整權限文案；過渡期內用較短文案 */
    approvedModalBody() {
      return this.$t(
        isKybLockPeriodActiveByCache()
          ? 'lbl_EMPBE_BR_ApprovedModal_BodyAfterLock'
          : 'lbl_EMPBE_BR_ApprovedModal_Body',
      );
    },
  },
  watch: {
    brUserKey: {
      immediate: true,
      handler(val, oldVal) {
        if (!val) return;
        if (!oldVal || oldVal !== val) {
          if (oldVal && oldVal !== val) {
            invalidateBrRuntimeCache();
            markBrPostLoginPromptPending();
          }
          this.$nextTick(() => {
            this.tryBootstrapBrPrompts();
          });
        }
      },
    },
    '$route.path'(newPath, oldPath) {
      this.ensureLockedLanding();
      if (!this.brUserKey) return;
      // 规格：弹窗只在供应商中心首页展示（进入 / 刷新首页）
      const enteredHome = isBrHomeRoute(newPath) && !isBrHomeRoute(oldPath);
      if (enteredHome) {
        this.tryBootstrapBrPrompts();
      }
    },
  },
  mounted() {
    if (!this.brUserKey) return;
    this.tryBootstrapBrPrompts();
  },
  methods: {
    /**
     * 触发条件（对齐产品）：
     * · BR_REMINDER_ENABLED=false → 全部弹框不展示
     * · 仅供应商中心首页
     * · 每次登录仅一款弹窗（session 标记，同登录不连弹）
     * · 优先序：待弹驳回 > 待弹通过 → 即将过期 → 提交提醒
     * · 关掉结果窗只 ack 当前这一笔 ApprovalId；其它待弹留给下次登录
     * · 临期弹窗：每公司每天最多一次（localStorage，登入/登出不重置）
     */
    tryBootstrapBrPrompts() {
      if (!isBrReminderEnabled()) {
        this.showReminder = false;
        this.showApproved = false;
        this.showRejected = false;
        this.showExpiringSoon = false;
        this.ensureLockedLanding();
        return;
      }
      const onHome = isBrHomeRoute(this.$route.path);
      if (!onHome) {
        // 非首页不消费登录标记，避免锁定落地到 BR 页时丢掉「待弹」机会
        this.ensureLockedLanding();
        return;
      }
      const fromLogin = consumeBrPostLoginPromptPending();
      // 本登录已展示过任一弹窗 → 不再弹（含从首页进 BR 再回首页）
      if (!fromLogin && hasBrPromptShownThisLogin()) {
        this.ensureLockedLanding();
        return;
      }
      this.bootstrapBrPrompts();
    },
    async bootstrapBrPrompts() {
      // 本登录已弹过：不要先清掉正在显示的窗，再因 session 标记直接 return
      if (hasBrPromptShownThisLogin()) {
        this.ensureLockedLanding();
        return;
      }
      const token = ++this.bootstrapToken;
      this.showReminder = false;
      this.showApproved = false;
      this.showRejected = false;
      this.showExpiringSoon = false;
      this.shownResultApprovalId = '';

      // soft-force：与 router 5s 内去重；invalidate 后仍会真刷新
      await loadBrSubmissionStatus(true);
      if (token !== this.bootstrapToken || !this.user) return;

      this.ensureLockedLanding();
      if (hasBrPromptShownThisLogin()) {
        return;
      }

      const notification = detectBrStatusNotification(this.user, brRuntimeState.documentList);

      // 优先：驳回 > 通过；弹出不立刻 ack 全部，关掉后只标当前这一笔已读
      if (notification && notification.type === 'rejected') {
        this.shownResultApprovalId = notification.approvalId;
        this.rejectedModalBody = this.$t('lbl_EMPBE_BR_RejectedModal_Body');
        this.showRejected = true;
        markBrPromptShownThisLogin();
        return;
      }
      if (notification && notification.type === 'approved') {
        this.shownResultApprovalId = notification.approvalId;
        this.showApproved = true;
        markBrPromptShownThisLogin();
        return;
      }

      const expirySoonItem = detectBrExpirySoonNotification(this.user, brRuntimeState.documentList);
      if (expirySoonItem && !this.deferExpiryModalThisSession) {
        this.expiringSoonModalBody = this.$t('lbl_EMPBE_BR_ExpirySoonModal_Body');
        this.showExpiringSoon = true;
        // 弹出即记「今日已提醒」，避免同日反复登入/登出再弹
        dismissBrExpirySoonForToday(this.user);
        markBrPromptShownThisLogin();
        return;
      }

      if (shouldShowBrReminder(this.user, this.$route)) {
        this.showReminder = true;
        markBrPromptShownThisLogin();
      }
    },
    ensureLockedLanding() {
      const redirect = shouldRedirectLockedBrRoute(this.user, this.$route);
      if (redirect && this.$route.path !== redirect) {
        this.$router.replace(redirect).catch(() => {});
      }
    },
    closeStatus(kind, route) {
      this.deferExpiryModalThisSession = true;
      const approvalId = this.shownResultApprovalId;
      this.shownResultApprovalId = '';
      acknowledgeBrStatusNotification(this.user, kind, brRuntimeState.documentList, approvalId);
      this.showApproved = false;
      this.showRejected = false;
      if (route) this.$router.push(route).catch(() => {});
    },
    closeExpiry(route) {
      acknowledgeBrExpirySoonNotification(this.user);
      this.showExpiringSoon = false;
      if (route) this.$router.push(route).catch(() => {});
    },
    handleLater() {
      dismissBrReminderForSession();
      this.showReminder = false;
      this.ensureLockedLanding();
    },
    handleSubmitNow() {
      dismissBrReminderForSession();
      this.showReminder = false;
      if (this.$route.path.indexOf('/businessDocuments') !== 0) {
        this.$router.push('/businessDocuments/addDocument');
      }
    },
  },
};
</script>
